import React, { useState } from "react";
import Grid from "@material-ui/core/Grid";
import { EditorState, ContentState } from "draft-js";
import htmlToDraft from "html-to-draftjs";
import { Editor } from "react-draft-wysiwyg";
import Button from "components/Button";
import TextField from "components/TextField";
import Select from "components/Select";
import Checkbox from "components/CheckBox";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import LogoImage from "assets/images/logo.png";
import { DECORATING_TECHNIQUES } from "shared/constants/decorating-techniques";
import {
  Container,
  ProductInformation,
  SelectCategories,
  Update,
  CloudWrapper,
} from "styles/pages/edit-products/editProductInformation";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

const isImageValid = (url) =>
  url?.includes(".png") || url?.includes(".jpeg") || url?.includes(".jpg");

const openCloudinaryWidget = (folder, cb) => {
  const myWidget = window.cloudinary.createUploadWidget(
    {
      cloudName: "big-oven-tees-inc",
      uploadPreset: "tnedst8q",
      folder,
    },
    (error, result) => {
      if (!error && result && result.event === "success") {
        cb(result?.info?.secure_url);
        myWidget.close();
      }
    }
  );
  myWidget.open();
};

const initiateEditorState = (html) => {
  if (!html) {
    return EditorState.createEmpty();
  }

  const contentBlock = htmlToDraft(html);
  const contentState = ContentState.createFromBlockArray(
    contentBlock.contentBlocks
  );
  return EditorState.createWithContent(contentState);
};

const EditProductInformation = ({
  categories,
  styleTags,
  product = { Product: {} },
  brandsTags,
  promosTags,
  updateProductInformation,
}) => {
  const [state, setState] = useState({
    ID: product?.Product?.ID,
    Index: 0,
    Active: true,
    LowestPrice: 0,
    SKU: product?.Product?.SKU || "",
    PrintType: product?.Product?.PrintType || "",
    Name: product?.Product?.Name || "",
    DecorationNeeded:
      product?.Product?.DecorationNeeded === false ? false : true,
    DisplayName: product?.Product?.DisplayName || "",
    Icon: product?.Product?.Icon || "",
    MockupPath: product?.Product?.MockupPath || "",
    ArtworkSpecsPath: product?.Product?.ArtworkSpecsPath || "",
    SizeChartPath: product?.Product?.SizeChartPath || "",
    MarketingImagePath: product?.Product?.MarketingImagePath || "",
    MarketingBackImagePath: product?.Product?.MarketingBackImagePath || "",
    Description: initiateEditorState(product?.Product?.Description),
    CategoryIds: product?.Product?.CategoryIds || [],
    ProductTags: product?.Product?.ProductTags || [],
    MarketingImageBase64: product?.Product?.MarketingImageBase64 || "",
    defaultMaximumFulfillDays: product?.Product?.defaultMaximumFulfillDays || 0,
    defaultMinimumFulfillDays: product?.Product?.defaultMinimumFulfillDays || 0,
    screenPrinting: !!product?.Product?.screenPrinting,
    weight: product?.Product?.weight || 0,
  });

  const isStyleTagSelected = () => {
    for (let i = 0; i < styleTags?.length; i++) {
      for (let j = 0; j < state?.ProductTags?.length; j++) {
        if (styleTags[i].Id === state?.ProductTags[j]?.TagId) {
          return true;
        }
      }
    }

    return false;
  };

  const disabled =
    !state?.SKU ||
    !state?.PrintType ||
    !state?.Name ||
    !state?.DisplayName ||
    !state?.MarketingImagePath ||
    !state?.CategoryIds?.length ||
    !state?.defaultMaximumFulfillDays ||
    !state?.defaultMinimumFulfillDays ||
    !state?.weight ||
    !isStyleTagSelected();

  return (
    <Container>
      <ProductInformation>
        <h3>Product Information</h3>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3} md={3}>
            <TextField
              label="SKU"
              size="small"
              value={state?.SKU}
              onChange={(SKU) =>
                setState({
                  ...state,
                  SKU,
                })
              }
            />
          </Grid>

          <Grid item xs={6} sm={3} md={3}>
            <TextField
              label="Name"
              size="small"
              value={state?.Name}
              onChange={(Name) =>
                setState({
                  ...state,
                  Name,
                })
              }
            />
          </Grid>
          <Grid item xs={6} sm={3} md={3}>
            <TextField
              label="Display Name"
              size="small"
              value={state?.DisplayName}
              onChange={(DisplayName) =>
                setState({
                  ...state,
                  DisplayName,
                })
              }
            />
          </Grid>
          <Grid item xs={6} sm={3} md={4}>
            <CloudWrapper>
              <img
                src={
                  isImageValid(state?.MockupPath)
                    ? state?.MockupPath
                    : LogoImage
                }
              />
              <TextField
                label="Mockup Path"
                size="small"
                value={state?.MockupPath}
                onChange={(MockupPath) =>
                  setState({
                    ...state,
                    MockupPath,
                  })
                }
              />
              <CloudUploadIcon
                onClick={() => {
                  openCloudinaryWidget(
                    `Shirtly/Shirtly_Products_Mock/Images/mockups/${state.Name}`,
                    (secureUrl) => {
                      setState({
                        ...state,
                        MockupPath: secureUrl,
                      });
                    }
                  );
                }}
              />
            </CloudWrapper>
          </Grid>
          <Grid item xs={6} sm={3} md={4}>
            <CloudWrapper>
              <img
                src={
                  isImageValid(state?.ArtworkSpecsPath)
                    ? state?.ArtworkSpecsPath
                    : LogoImage
                }
              />
              <TextField
                label="Artwork Specs Path"
                size="small"
                value={state?.ArtworkSpecsPath}
                onChange={(ArtworkSpecsPath) =>
                  setState({
                    ...state,
                    ArtworkSpecsPath,
                  })
                }
              />
              <CloudUploadIcon
                onClick={() => {
                  openCloudinaryWidget(
                    `Shirtly/Shirtly_Products_Mock/Images/mockups/${state.Name}`,
                    (secureUrl) => {
                      setState({
                        ...state,
                        ArtworkSpecsPath: secureUrl,
                      });
                    }
                  );
                }}
              />
            </CloudWrapper>
          </Grid>
          <Grid item xs={6} sm={3} md={4}>
            <CloudWrapper>
              <img
                src={
                  isImageValid(state?.SizeChartPath)
                    ? state?.SizeChartPath
                    : LogoImage
                }
              />
              <TextField
                label="Size Chart Path"
                size="small"
                value={state?.SizeChartPath}
                onChange={(SizeChartPath) =>
                  setState({
                    ...state,
                    SizeChartPath,
                  })
                }
              />
              <CloudUploadIcon
                onClick={() => {
                  openCloudinaryWidget(
                    `Shirtly/Shirtly_Products_Mock/Images/mockups/${state.Name}`,
                    (secureUrl) => {
                      setState({
                        ...state,
                        SizeChartPath: secureUrl,
                      });
                    }
                  );
                }}
              />
            </CloudWrapper>
          </Grid>
          <Grid item xs={6} sm={3} md={4}>
            <CloudWrapper>
              <img
                src={
                  isImageValid(state?.MarketingImagePath)
                    ? state?.MarketingImagePath
                    : LogoImage
                }
              />
              <TextField
                label="Marketing Image Path"
                size="small"
                value={state?.MarketingImagePath}
                onChange={(MarketingImagePath) =>
                  setState({
                    ...state,
                    MarketingImagePath,
                  })
                }
              />
              <CloudUploadIcon
                onClick={() => {
                  openCloudinaryWidget(
                    `Shirtly/Shirtly_Products_Mock/Images/mockups/${state.Name}`,
                    (secureUrl) => {
                      setState({
                        ...state,
                        MarketingImagePath: secureUrl,
                      });
                    }
                  );
                }}
              />
            </CloudWrapper>
          </Grid>

          {state?.PrintType === "SUB" && (
            <Grid item xs={6} sm={3} md={4}>
              <CloudWrapper>
                <img
                  src={
                    isImageValid(state?.MarketingImageBase64)
                      ? state?.MarketingImageBase64
                      : LogoImage
                  }
                />
                <TextField
                  label="Marketing Image Base64"
                  size="small"
                  value={state?.MarketingImageBase64}
                  onChange={(MarketingImageBase64) =>
                    setState({
                      ...state,
                      MarketingImageBase64,
                    })
                  }
                />
                <CloudUploadIcon
                  onClick={() => {
                    openCloudinaryWidget(
                      `Shirtly/Shirtly_Products_Mock/Images/mockups/${state.Name}`,
                      (secureUrl) => {
                        setState({
                          ...state,
                          MarketingImageBase64: secureUrl,
                        });
                      }
                    );
                  }}
                />
              </CloudWrapper>
            </Grid>
          )}

          <Grid item xs={6} sm={3} md={4}>
            <CloudWrapper>
              <img
                src={
                  isImageValid(state?.MarketingBackImagePath)
                    ? state?.MarketingBackImagePath
                    : LogoImage
                }
              />
              <TextField
                label="Marketing Back Image Path"
                size="small"
                value={state?.MarketingBackImagePath}
                onChange={(MarketingBackImagePath) =>
                  setState({
                    ...state,
                    MarketingBackImagePath,
                  })
                }
              />
              <CloudUploadIcon
                onClick={() => {
                  openCloudinaryWidget(
                    `Shirtly/Shirtly_Products_Mock/Images/mockups/${state.Name}`,
                    (secureUrl) => {
                      setState({
                        ...state,
                        MarketingBackImagePath: secureUrl,
                      });
                    }
                  );
                }}
              />
            </CloudWrapper>
          </Grid>

          <Grid item xs={6} sm={3} md={4}>
            <CloudWrapper>
              <img src={isImageValid(state?.Icon) ? state?.Icon : LogoImage} />
              <TextField
                label="Icon URL"
                size="small"
                value={state?.Icon}
                onChange={(Icon) =>
                  setState({
                    ...state,
                    Icon,
                  })
                }
              />
              <CloudUploadIcon
                onClick={() => {
                  openCloudinaryWidget(
                    `Shirtly/Shirtly_Products_Mock/Images/mockups/${state.Name}`,
                    (secureUrl) => {
                      setState({
                        ...state,
                        Icon: secureUrl,
                      });
                    }
                  );
                }}
              />
            </CloudWrapper>
          </Grid>

          <Grid item xs={12} sm={4} md={4}>
            <Select
              options={DECORATING_TECHNIQUES}
              labelKey="DisplayName"
              valueKey="DisplayName"
              label="Print Type"
              value={{ DisplayName: state?.PrintType }}
              onChange={(PrintType) =>
                setState({
                  ...state,
                  PrintType,
                })
              }
            />
          </Grid>

          <Grid item xs={12} sm={4} md={4}>
            <TextField
              label="Minimum Fulfillment Days"
              type="number"
              size="small"
              value={state?.defaultMinimumFulfillDays}
              onChange={(defaultMinimumFulfillDays) =>
                setState({
                  ...state,
                  defaultMinimumFulfillDays: Number(defaultMinimumFulfillDays),
                })
              }
            />
          </Grid>

          <Grid item xs={12} sm={4} md={4}>
            <TextField
              label="Maximum Fulfillment Days"
              size="small"
              type="number"
              value={state?.defaultMaximumFulfillDays}
              onChange={(defaultMaximumFulfillDays) =>
                setState({
                  ...state,
                  defaultMaximumFulfillDays: Number(defaultMaximumFulfillDays),
                })
              }
            />
          </Grid>

          <Grid item xs={12} sm={4} md={4}>
            <TextField
              label="Weight (In Pounds)"
              size="small"
              type="number"
              value={state?.weight}
              onChange={(weight) =>
                setState({
                  ...state,
                  weight: Number(weight),
                })
              }
            />
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <h3 style={{ marginBottom: 10 }}>Description</h3>
            <Editor
              editorState={state?.Description}
              onEditorStateChange={(Description) =>
                setState({
                  ...state,
                  Description,
                })
              }
            />
          </Grid>
        </Grid>
      </ProductInformation>

      <SelectCategories>
        <h3>Decoration Compulsory</h3>
        <Grid container spacing={1}>
          <Grid item xs={6} sm={3} md={2}>
            <Checkbox
              label="Yes"
              checked={state?.DecorationNeeded === true}
              onChange={(v) => {
                setState({
                  ...state,
                  DecorationNeeded: true,
                });
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Checkbox
              label="No"
              checked={state?.DecorationNeeded === false}
              onChange={(v) => {
                setState({
                  ...state,
                  DecorationNeeded: false,
                });
              }}
            />
          </Grid>
        </Grid>
      </SelectCategories>

      <SelectCategories>
        <h3>Select Categories</h3>
        <Grid container spacing={1}>
          {categories?.map((category, index) => {
            const checked = !!state?.CategoryIds?.find(
              (c) => c.CatId === category?.CatId
            );
            return (
              <Grid item xs={6} sm={3} md={2} key={`category${index}`}>
                <Checkbox
                  label={category?.DisplayName}
                  key={category?.DisplayName + category?.CatId}
                  checked={checked}
                  onChange={(v) => {
                    if (v) {
                      setState({
                        ...state,
                        CategoryIds: [...state?.CategoryIds, category],
                      });
                    } else {
                      setState({
                        ...state,
                        CategoryIds: state?.CategoryIds?.filter(
                          (c) => c.CatId !== category?.CatId
                        ),
                      });
                    }
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      </SelectCategories>

      <div>
        <h3>Select Style Tags</h3>
        <Grid container spacing={1}>
          {styleTags?.map((tag, index) => {
            const checked = !!state?.ProductTags?.find(
              (c) => c.TagId === tag?.Id
            );
            return (
              <Grid item xs={6} sm={3} md={2} key={`tag${index}`}>
                <Checkbox
                  label={tag?.DisplayName}
                  key={tag?.DisplayName + tag?.Id}
                  checked={checked}
                  onChange={(v) => {
                    if (v) {
                      setState({
                        ...state,
                        ProductTags: [
                          ...state?.ProductTags,
                          { ...tag, TagId: tag?.Id },
                        ],
                      });
                    } else {
                      setState({
                        ...state,
                        ProductTags: state?.ProductTags?.filter(
                          (c) => c.TagId !== tag?.Id
                        ),
                      });
                    }
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      </div>

      <div>
        <h3>Select Brand Tags</h3>
        <Grid container spacing={1}>
          {brandsTags?.map((tag, index) => {
            const checked = !!state?.ProductTags?.find(
              (c) => c.TagId === tag?.Id
            );
            return (
              <Grid item xs={6} sm={4} md={3} key={`productTags${index}`}>
                <Checkbox
                  label={tag?.DisplayName}
                  key={tag?.DisplayName + tag?.Id}
                  checked={checked}
                  onChange={(v) => {
                    if (v) {
                      setState({
                        ...state,
                        ProductTags: [
                          ...state?.ProductTags,
                          { ...tag, TagId: tag?.Id },
                        ],
                      });
                    } else {
                      setState({
                        ...state,
                        ProductTags: state?.ProductTags?.filter(
                          (c) => c.TagId !== tag?.Id
                        ),
                      });
                    }
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      </div>

      <div>
        <h3>Select Promos Tags</h3>
        <Grid container spacing={1}>
          {promosTags?.map((tag, index) => {
            const checked = !!state?.ProductTags?.find(
              (c) => c.TagId === tag?.Id
            );
            return (
              <Grid item xs={6} sm={3} md={2} key={`promoTags${index}`}>
                <Checkbox
                  label={tag?.DisplayName}
                  key={tag?.DisplayName + tag?.Id}
                  checked={checked}
                  onChange={(v) => {
                    if (v) {
                      setState({
                        ...state,
                        ProductTags: [
                          ...state?.ProductTags,
                          { ...tag, TagId: tag?.Id },
                        ],
                      });
                    } else {
                      setState({
                        ...state,
                        ProductTags: state?.ProductTags?.filter(
                          (c) => c.TagId !== tag?.Id
                        ),
                      });
                    }
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      </div>
      <Update>
        <Button
          title="Update"
          disabled={disabled}
          onClick={() => updateProductInformation(state)}
        />
      </Update>
    </Container>
  );
};

export default EditProductInformation;
